import { useIsHeidi2ClinicianMaxBetaEnabled } from '@/features/flags';
import { currentPlanDisplayMessages } from '@/features/settings/billing/hooks/current-plan-display.messages';
import type { QueryUserBillingInfoResponse } from '@/hooks/query/useQueryUserBillingInfo';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useShouldHideMarketRestrictedFeatures } from '@/hooks/useShouldHideMarketRestrictedFeatures';
import { useUserQuota } from '@/hooks/useUserQuota';
import { tz } from '@date-fns/tz';
import {
  isClinicianMaxBillingSeat,
  isClinicianStandardBillingSeat,
} from '@heidi/commerce';
import { Period } from '@heidi/commerce/contracts';
import { useTranslations } from '@heidi/runtime/i18n';
import {
  BillingSeatType,
  type SubscriptionStatus,
  SubscriptionTier,
} from '@heidi/schema/ml-scribe';
import { isValid, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export type CurrentPlanDisplayResult = {
  planName: string;
  description: string;
  billingPeriodLabel: string | null;
  nextBillingDateFormatted: string | null;
  isPaidTier: boolean;
  currentPaidTier: SubscriptionTier | null | undefined;
  billingInfo: QueryUserBillingInfoResponse | undefined;
  previousStatusBeforeClinicianTrial?: SubscriptionStatus | null;
};

type UseCurrentPlanDisplayOptions = {
  isHeidi2OptInEnabled?: boolean;
};

export function useCurrentPlanDisplay({
  isHeidi2OptInEnabled = false,
}: UseCurrentPlanDisplayOptions = {}): CurrentPlanDisplayResult {
  const { t } = useTranslations();
  const intl = useIntl();
  const isClinicianMaxBetaEnabled = useIsHeidi2ClinicianMaxBetaEnabled();
  const { userTier } = useQueryUserTier();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { shouldHide: shouldHideInSessionEvidenceUsage } =
    useShouldHideMarketRestrictedFeatures();
  const { quotaResetDate, customNoteQuotaRemaining, inSessionQuotaRemaining } =
    useUserQuota();

  const isFakeClinicianTrial = billingInfo?.is_fake_clinician_trial;

  // if the user is on a fake clinician trial, use the paid tier instead of the user tier
  const currentPaidTier = isFakeClinicianTrial ? billingInfo?.tier : userTier;

  const isPaidTier = currentPaidTier != null && currentPaidTier !== SubscriptionTier.FREE;
  // Practice Max is a MAX seat on either Practice tier: `CLINICIAN_PRO_PLUS`
  // under the evidence-launch flows, legacy `PRO_PLUS` otherwise (and on a
  // fake trial, which reads `billingInfo.tier`). The seat decides, not the tier.
  const isPracticeMaxSeat =
    isHeidi2OptInEnabled && billingInfo?.seat_type === BillingSeatType.MAX;

  const planName = useMemo(() => {
    switch (currentPaidTier) {
      case SubscriptionTier.FREE:
        return t('tier.free');
      case SubscriptionTier.PRO:
        return t('tier.pro');
      case SubscriptionTier.PRO_PLUS:
        if (isHeidi2OptInEnabled) {
          return intl.formatMessage(
            isPracticeMaxSeat
              ? currentPlanDisplayMessages.practiceMaxPlanName
              : currentPlanDisplayMessages.practiceStandardPlanName
          );
        }
        return t('tier.scribeTeam');
      case SubscriptionTier.EVIDENCE:
        return t('tier.evidencePlus');
      case SubscriptionTier.EVIDENCE_TEAM:
        return t('tier.evidenceTeam');
      case SubscriptionTier.CLINICIAN_PRO:
        if (isClinicianMaxBetaEnabled || isHeidi2OptInEnabled) {
          if (isClinicianStandardBillingSeat(billingInfo)) {
            return intl.formatMessage(currentPlanDisplayMessages.clinicianVisitPlanName);
          }
          if (isClinicianMaxBillingSeat(billingInfo)) {
            return intl.formatMessage(currentPlanDisplayMessages.clinicianMaxPlanName);
          }
        }
        return t('tier.clinician');
      case SubscriptionTier.CLINICIAN_PRO_PLUS:
        if (isHeidi2OptInEnabled) {
          return intl.formatMessage(
            isPracticeMaxSeat
              ? currentPlanDisplayMessages.practiceMaxPlanName
              : currentPlanDisplayMessages.practiceStandardPlanName
          );
        }
        return t('tier.practice');
      case SubscriptionTier.ENTERPRISE:
      case SubscriptionTier.CLINICIAN_ENTERPRISE:
        return t('tier.enterprise');
      default:
        return t('tier.free');
    }
  }, [
    billingInfo,
    currentPaidTier,
    intl,
    isClinicianMaxBetaEnabled,
    isHeidi2OptInEnabled,
    isPracticeMaxSeat,
    t,
  ]);

  const description = useMemo(() => {
    switch (currentPaidTier) {
      case SubscriptionTier.FREE:
        if (shouldHideInSessionEvidenceUsage) {
          return t(
            'settings.billing.currentPlanSection.customNoteActionsPlanDescription',
            {
              customNoteActions: customNoteQuotaRemaining,
              date: quotaResetDate,
            }
          );
        }
        return t('settings.billing.currentPlanSection.freePlanDescription', {
          customNoteActions: customNoteQuotaRemaining,
          inSessionActions: inSessionQuotaRemaining,
          date: quotaResetDate,
        });
      case SubscriptionTier.PRO:
        if (shouldHideInSessionEvidenceUsage) {
          return '';
        }
        return t('settings.billing.currentPlanSection.scribePlusPlanDescription', {
          inSessionActions: inSessionQuotaRemaining,
          date: quotaResetDate,
        });
      case SubscriptionTier.PRO_PLUS:
        if (isPracticeMaxSeat) {
          return intl.formatMessage(
            currentPlanDisplayMessages.practiceMaxPlanDescription
          );
        }
        return t('settings.billing.currentPlanSection.scribeTeamPlanDescription');
      case SubscriptionTier.EVIDENCE:
        if (shouldHideInSessionEvidenceUsage) {
          return '';
        }
        return t('settings.billing.currentPlanSection.evidencePlanDescription', {
          inSessionActions: inSessionQuotaRemaining,
          date: quotaResetDate,
        });
      case SubscriptionTier.CLINICIAN_PRO:
        return t('settings.billing.currentPlanSection.clinicianPlanDescription');
      case SubscriptionTier.CLINICIAN_PRO_PLUS:
        if (isPracticeMaxSeat) {
          return intl.formatMessage(
            currentPlanDisplayMessages.practiceMaxPlanDescription
          );
        }
        return t('settings.billing.currentPlanSection.practicePlanDescription');
      case SubscriptionTier.CLINICIAN_ENTERPRISE:
      case SubscriptionTier.ENTERPRISE:
        return t('settings.billing.currentPlanSection.enterprisePlanDescription');
      default:
        return '';
    }
  }, [
    t,
    intl,
    currentPaidTier,
    customNoteQuotaRemaining,
    inSessionQuotaRemaining,
    isPracticeMaxSeat,
    quotaResetDate,
    shouldHideInSessionEvidenceUsage,
  ]);

  const billingPeriodLabel = useMemo(() => {
    if (!isPaidTier || !billingInfo?.plan_cycle) return null;
    return billingInfo.plan_cycle === Period.ONE_MONTH
      ? t('settings.billing.currentPlanSection.monthlyPlan')
      : t('settings.billing.currentPlanSection.yearlyPlan');
  }, [isPaidTier, billingInfo?.plan_cycle, t]);

  const nextBillingDateFormatted = useMemo(() => {
    if (!isPaidTier || !billingInfo?.next_billing_date) return null;
    // Guard against malformed BE strings — date-fns `format` throws on
    // Invalid Date (unlike moment's softer "Invalid date" string).
    // `in: tz('UTC')` reads date-only and zone-less values as UTC, not runtime-local.
    const parsed = parseISO(billingInfo.next_billing_date, { in: tz('UTC') });
    if (!isValid(parsed)) return null;
    // Stripe bills on the UTC calendar day; local-tz formatting shows the day
    // before for anyone behind UTC.
    return intl.formatDate(parsed, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }, [isPaidTier, billingInfo?.next_billing_date, intl]);

  return {
    planName,
    description,
    billingPeriodLabel,
    nextBillingDateFormatted,
    isPaidTier,
    currentPaidTier,
    billingInfo,
    previousStatusBeforeClinicianTrial:
      billingInfo?.previous_status_before_clinician_trial,
  };
}
